import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/AuthContext";
import { useAuth } from "@/AuthContext";
import { toast } from "sonner";
import {
  Plus, X, CaretLeft, CaretRight, Envelope, Link as LinkIcon, Trash,
  ListBullets, MagnifyingGlass, Eye, DownloadSimple, User as UserIcon,
  Calendar as CalendarIcon, FileText, Bell
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { DocumentViewerInline } from "../components/DocumentViewer";
import ExportButton from "../components/ExportButton";

const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const DAYS_ES = ["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"];

const STATUS_OPTIONS = [
  { k:"programado", l:"Programado", c:"bg-blue-50 text-blue-700 border-blue-200" },
  { k:"en_proceso", l:"En proceso", c:"bg-amber-50 text-amber-700 border-amber-200" },
  { k:"completado", l:"Completado", c:"bg-emerald-50 text-emerald-700 border-emerald-200" },
  { k:"vencido", l:"Vencido", c:"bg-rose-50 text-rose-700 border-rose-200" },
];

export default function CalendarPage() {
  const { user, can } = useAuth();
  const [events, setEvents] = useState([]);
  const [docs, setDocs] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState("mensual"); // diaria, semanal, mensual, anual, lista
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  // list view filters
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const canEdit = can("calendar", "write");
  const canDelete = can("calendar", "delete");

  const reload = () => {
    api.get("/calendar/events").then(r => setEvents(r.data)).catch(()=>{});
    api.get("/documents").then(r => setDocs(r.data)).catch(()=>{});
    api.get("/catalogs", { params: { type: "frecuencia_notificacion" } }).then(r => setFrequencies(r.data)).catch(()=>{});
  };
  useEffect(() => { reload(); }, []);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = lastDay.getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const eventsByDay = {};
  events.forEach(ev => {
    const dt = ev.event_date;
    eventsByDay[dt] = eventsByDay[dt] || [];
    eventsByDay[dt].push(ev);
  });
  const fmt = (d) => d.toISOString().slice(0,10);

  const filteredList = useMemo(() => {
    const s = searchTerm.toLowerCase();
    return events
      .filter(ev => !filterStatus || (ev.status||"programado") === filterStatus)
      .filter(ev => !s || ev.title.toLowerCase().includes(s) || (ev.description||"").toLowerCase().includes(s))
      .sort((a,b) => a.event_date.localeCompare(b.event_date));
  }, [events, searchTerm, filterStatus]);

  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar este evento?")) return;
    try { await api.delete(`/calendar/events/${id}`); toast.success("Evento eliminado"); setSelectedEvent(null); reload(); }
    catch { toast.error("Error al eliminar"); }
  };

  const onTest = async (id) => {
    try { await api.post(`/calendar/events/${id}/test-notification`); toast.success("Correo de prueba enviado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Módulo</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Calendario de vencimientos</h1>
          <p className="text-neutral-500 mt-2">Programa eventos, liga documentos y activa notificaciones vía Mailjet.</p>
        </div>
        {canEdit ? (
          <div className="flex gap-2">
            <ExportButton resource="calendar"/>
            <button onClick={()=>setWizardOpen(true)} data-testid="open-event-wizard-btn"
              className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md">
              <Plus size={18} weight="bold" /> Nuevo evento
            </button>
          </div>
        ) : <ExportButton resource="calendar"/>}
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          {k:"diaria", l:"Diaria"},
          {k:"semanal", l:"Semanal"},
          {k:"mensual", l:"Mensual"},
          {k:"anual", l:"Anual"},
          {k:"lista", l:"Lista", icon: ListBullets},
        ].map(v => (
          <button key={v.k} onClick={()=>setView(v.k)} className={`px-4 py-2 text-sm font-semibold rounded-md inline-flex items-center gap-1.5 ${view===v.k?'bg-blue-600 text-white':'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'}`} data-testid={`view-${v.k}`}>
            {v.icon && <v.icon size={14}/>} {v.l}
          </button>
        ))}
      </div>

      {view==="mensual" && (
      <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-neutral-200">
          <button onClick={()=>setCursor(new Date(year, month-1, 1))} className="p-2 hover:bg-neutral-100 rounded-md" data-testid="prev-month-btn"><CaretLeft size={20}/></button>
          <h2 className="font-display text-2xl font-bold tracking-tight">{MONTHS_ES[month]} <span className="text-neutral-400">{year}</span></h2>
          <button onClick={()=>setCursor(new Date(year, month+1, 1))} className="p-2 hover:bg-neutral-100 rounded-md" data-testid="next-month-btn"><CaretRight size={20}/></button>
        </div>
        <div className="grid grid-cols-7 border-b border-neutral-200">
          {DAYS_ES.map(d => <div key={d} className="text-xs uppercase tracking-widest font-semibold text-neutral-500 p-3 text-center border-r border-neutral-200 last:border-r-0">{d}</div>)}
        </div>
        <div className="grid grid-cols-7" data-testid="calendar-grid">
          {cells.map((d, i) => {
            const isToday = d && fmt(d) === fmt(new Date());
            const dayEvents = d ? (eventsByDay[fmt(d)] || []) : [];
            return (
              <div key={i} className={`min-h-[110px] border-r border-b border-neutral-100 p-2 ${!d ? 'bg-neutral-50/50' : 'bg-white'}`}>
                {d && (
                  <>
                    <div className={`text-xs font-bold mb-1 ${isToday ? 'inline-flex w-6 h-6 rounded-full bg-blue-600 text-white items-center justify-center' : 'text-neutral-700'}`}>
                      {d.getDate()}
                    </div>
                    <div className="space-y-1">
                      {dayEvents.slice(0,3).map(ev => {
                        const days = Math.ceil((new Date(ev.event_date) - new Date()) / 86400000);
                        const color = days < 0 ? "bg-rose-500" : days <= 7 ? "bg-amber-500" : "bg-emerald-500";
                        return (
                          <button key={ev.id} onClick={()=>setSelectedEvent(ev)}
                                  className={`block w-full text-left ${color} text-white text-xs px-1.5 py-0.5 rounded truncate hover:opacity-90`}
                                  data-testid={`cal-event-${ev.id}`}>
                            {ev.title}
                          </button>
                        );
                      })}
                      {dayEvents.length > 3 && <div className="text-xs text-neutral-500">+{dayEvents.length-3} más</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
      )}

      {view==="diaria" && (
        <div className="bg-white border border-neutral-200 rounded-md p-6" data-testid="view-diaria-panel">
          <h2 className="font-display text-2xl font-bold tracking-tight mb-4">{fmt(new Date())}</h2>
          <ul className="space-y-2">
            {(eventsByDay[fmt(new Date())]||[]).map(ev=>(
              <li key={ev.id} onClick={()=>setSelectedEvent(ev)} className="p-3 bg-neutral-50 rounded-md cursor-pointer hover:bg-neutral-100 flex items-center justify-between">
                <div><div className="font-semibold">{ev.title}</div><div className="text-xs text-neutral-500">{ev.alarm_level||"manual"}</div></div>
                <span className="text-xs text-neutral-500">{ev.event_date}</span>
              </li>
            ))}
            {!(eventsByDay[fmt(new Date())]||[]).length && <p className="text-center py-12 text-neutral-400">Sin eventos hoy.</p>}
          </ul>
        </div>
      )}

      {view==="semanal" && (
        <div className="bg-white border border-neutral-200 rounded-md p-6" data-testid="view-semanal-panel">
          <h2 className="font-display text-2xl font-bold tracking-tight mb-4">Próximos 7 días</h2>
          <ul className="space-y-2">
            {events.filter(e=>{ const d=new Date(e.event_date),now=new Date(); return d>=now && (d-now)<7*86400000; }).map(ev=>(
              <li key={ev.id} onClick={()=>setSelectedEvent(ev)} className="p-3 bg-neutral-50 rounded-md cursor-pointer hover:bg-neutral-100 flex items-center justify-between" data-testid={`week-event-${ev.id}`}>
                <div><div className="font-semibold">{ev.title}</div><div className="text-xs text-neutral-500">{ev.event_date}</div></div>
                <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded">{ev.alarm_level||"manual"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {view==="anual" && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="view-anual-panel">
          {MONTHS_ES.map((m,idx)=>{
            const monthEvents = events.filter(e=>{ const d=new Date(e.event_date); return d.getFullYear()===year && d.getMonth()===idx; });
            return (
              <button key={m} onClick={()=>{setCursor(new Date(year,idx,1)); setView("mensual");}} className="bg-white border border-neutral-200 rounded-md p-4 hover:-translate-y-0.5 transition-all text-left">
                <div className="font-display text-lg font-bold">{m}</div>
                <div className="text-xs text-neutral-500 mb-2">{year}</div>
                <div className="font-display text-3xl font-black tracking-tighter">{monthEvents.length}</div>
                <div className="text-xs text-neutral-500">eventos</div>
              </button>
            );
          })}
        </div>
      )}

      {view==="lista" && (
        <div className="bg-white border border-neutral-200 rounded-md overflow-hidden" data-testid="view-lista-panel">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 border-b border-neutral-200 bg-neutral-50">
            <div className="relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16}/>
              <input value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} placeholder="Buscar por título o descripción..." className="w-full pl-9 pr-3 py-2 border border-neutral-200 rounded-md text-sm bg-white" data-testid="list-search"/>
            </div>
            <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} className="border border-neutral-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="list-status-filter">
              <option value="">Todos los estatus</option>
              {STATUS_OPTIONS.map(s=><option key={s.k} value={s.k}>{s.l}</option>)}
            </select>
            <div className="text-sm text-neutral-500 inline-flex items-center justify-end font-semibold">{filteredList.length} evento(s)</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="events-list-table">
              <thead>
                <tr className="border-b border-neutral-200 text-left text-xs uppercase tracking-widest text-neutral-500">
                  <th className="px-5 py-3 font-semibold">Fecha</th>
                  <th className="px-5 py-3 font-semibold">Evento</th>
                  <th className="px-5 py-3 font-semibold">Estatus</th>
                  <th className="px-5 py-3 font-semibold">Documento</th>
                  <th className="px-5 py-3 font-semibold">Responsable</th>
                  <th className="px-5 py-3 font-semibold">Notificación</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map(ev => {
                  const days = Math.ceil((new Date(ev.event_date) - new Date()) / 86400000);
                  const status = STATUS_OPTIONS.find(s=>s.k===(ev.status||"programado")) || STATUS_OPTIONS[0];
                  const linkedDoc = ev.document_ids?.length ? docs.find(d=>d.id===ev.document_ids[0]) : null;
                  return (
                    <tr key={ev.id} onClick={()=>setSelectedEvent(ev)} className="border-b border-neutral-100 hover:bg-blue-50/40 cursor-pointer transition-colors" data-testid={`list-event-${ev.id}`}>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <div className="font-mono font-semibold text-neutral-900">{ev.event_date}</div>
                        <div className={`text-xs ${days<0?'text-rose-600':days<=7?'text-amber-600':'text-neutral-500'}`}>{days<0?`Vencido ${Math.abs(days)}d`:`En ${days}d`}</div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="font-semibold text-neutral-900">{ev.title}</div>
                        {ev.description && <div className="text-xs text-neutral-500 truncate max-w-md">{ev.description}</div>}
                      </td>
                      <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded text-xs font-semibold border ${status.c}`}>{status.l}</span></td>
                      <td className="px-5 py-3 text-xs text-neutral-600">{linkedDoc ? linkedDoc.name : "—"}</td>
                      <td className="px-5 py-3 text-xs text-neutral-600">{ev.responsible || "—"}</td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(ev.notify_frequencies||[]).map(f=><span key={f} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-semibold">{f}</span>)}
                          {ev.notify_daily && !ev.notify_frequencies?.length && <span className="px-1.5 py-0.5 bg-rose-50 text-rose-700 rounded text-[10px] font-semibold">Diaria</span>}
                          {ev.notify_weekly && !ev.notify_frequencies?.length && <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[10px] font-semibold">Semanal</span>}
                          {ev.notify_monthly && !ev.notify_frequencies?.length && <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded text-[10px] font-semibold">Mensual</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredList.length === 0 && (
                  <tr><td colSpan={6} className="py-12 text-center text-neutral-400">Sin eventos que coincidan con los filtros.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedEvent && <EventDetailModal event={selectedEvent} docs={docs} onClose={()=>setSelectedEvent(null)} onDelete={onDelete} onTest={onTest} canEdit={canEdit} canDelete={canDelete}/>}
      {wizardOpen && <EventWizard docs={docs} frequencies={frequencies} onClose={()=>setWizardOpen(false)} onSuccess={()=>{ setWizardOpen(false); reload(); }} />}
    </div>
  );
}

function EventDetailModal({ event, docs, onClose, onDelete, onTest, canEdit, canDelete }) {
  const linkedDocs = (event.document_ids||[]).map(did => docs.find(d => d.id === did)).filter(Boolean);
  const [previewDoc, setPreviewDoc] = useState(linkedDocs[0] || null);
  const status = STATUS_OPTIONS.find(s=>s.k===(event.status||"programado")) || STATUS_OPTIONS[0];
  const days = Math.ceil((new Date(event.event_date) - new Date()) / 86400000);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="event-detail-modal">
      <div className="bg-white rounded-md w-full max-w-5xl border border-neutral-200 max-h-[95vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-start p-5 border-b border-neutral-200">
          <div className="min-w-0">
            <div className="flex flex-wrap gap-2 mb-2">
              <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${status.c}`} data-testid="event-status-badge">{status.l}</span>
              {days<0 && <span className="px-2 py-0.5 rounded text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">Vencido {Math.abs(days)} días</span>}
              {days>=0 && days<=7 && <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">En {days} días</span>}
            </div>
            <h3 className="font-display text-2xl font-bold tracking-tight truncate">{event.title}</h3>
            <p className="text-sm text-neutral-500 mt-1 inline-flex items-center gap-1.5"><CalendarIcon size={14}/> {event.event_date}</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700 flex-shrink-0" data-testid="event-detail-close"><X size={22}/></button>
        </div>

        <div className="grid lg:grid-cols-2 gap-0 flex-1 min-h-0 overflow-hidden">
          {/* Left: metadata */}
          <div className="p-5 space-y-4 overflow-y-auto border-r border-neutral-200">
            {event.description && (
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Descripción</div>
                <p className="text-sm text-neutral-700">{event.description}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Field label="Estado" value={status.l}/>
              <Field label="Responsable" value={event.responsible || "—"} icon={UserIcon}/>
              {event.alarm_level && <Field label="Nivel alarma" value={event.alarm_level}/>}
              {event.installation_id && <Field label="Instalación" value={event.installation_id.slice(0,8)+"…"}/>}
            </div>

            {(event.notify_frequencies?.length > 0 || event.notify_daily || event.notify_weekly || event.notify_monthly) && (
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2 inline-flex items-center gap-1.5"><Bell size={12}/> Frecuencia de notificación</div>
                <div className="flex flex-wrap gap-1.5">
                  {(event.notify_frequencies||[]).map(f=><span key={f} className="px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-semibold border border-blue-100">{f}</span>)}
                  {event.notify_daily && !event.notify_frequencies?.length && <span className="px-2 py-1 bg-rose-50 text-rose-700 rounded-md text-xs font-semibold border border-rose-100">Diaria</span>}
                  {event.notify_weekly && !event.notify_frequencies?.length && <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded-md text-xs font-semibold border border-amber-100">Semanal</span>}
                  {event.notify_monthly && !event.notify_frequencies?.length && <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded-md text-xs font-semibold border border-emerald-100">Mensual</span>}
                </div>
              </div>
            )}

            {event.notify_emails?.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Destinatarios</div>
                <div className="text-sm text-neutral-700">{event.notify_emails.join(", ")}</div>
              </div>
            )}

            {linkedDocs.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2 inline-flex items-center gap-1.5"><LinkIcon size={12}/> Documentos ligados</div>
                <ul className="space-y-1.5">
                  {linkedDocs.map(d => (
                    <li key={d.id} className={`p-2 rounded-md border text-sm flex items-center gap-2 cursor-pointer transition-colors ${previewDoc?.id===d.id?'bg-blue-50 border-blue-300':'bg-neutral-50 border-neutral-200 hover:bg-neutral-100'}`} onClick={()=>setPreviewDoc(d)} data-testid={`linked-doc-${d.id}`}>
                      <FileText size={14} className="text-blue-600 flex-shrink-0"/>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{d.name}</div>
                        <div className="text-xs text-neutral-500">{d.period} · {d.authority}</div>
                      </div>
                      <Link to={`/documentos/${d.id}`} onClick={e=>e.stopPropagation()} className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex-shrink-0">Abrir</Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right: preview */}
          <div className="p-5 overflow-y-auto bg-neutral-50/30 min-h-[300px]">
            {previewDoc ? (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold inline-flex items-center gap-1.5"><Eye size={12}/> Previsualización</div>
                  <a href={`/api/documents/file/${previewDoc.drive_file_id}`} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:text-blue-700 font-semibold inline-flex items-center gap-1"><DownloadSimple size={12}/> Descargar</a>
                </div>
                <DocumentViewerInline docId={previewDoc.id} fileId={previewDoc.drive_file_id} mimeType={previewDoc.mime_type} filename={previewDoc.filename}/>
              </>
            ) : (
              <div className="h-full min-h-[300px] flex items-center justify-center text-neutral-400 text-sm border border-dashed border-neutral-300 rounded-md">
                {linkedDocs.length === 0 ? "Sin documento vinculado a este evento." : "Selecciona un documento para previsualizar."}
              </div>
            )}
          </div>
        </div>

        {(canEdit || canDelete) && (
          <div className="flex justify-end gap-2 p-4 bg-neutral-50 border-t border-neutral-200">
            {canEdit && (
              <button onClick={()=>onTest(event.id)} className="px-3 py-2 text-sm border border-neutral-300 rounded-md hover:bg-white inline-flex items-center gap-1.5" data-testid="test-notify-btn">
                <Envelope size={14}/> Enviar prueba
              </button>
            )}
            {canDelete && (
              <button onClick={()=>onDelete(event.id)} className="px-3 py-2 text-sm bg-rose-500 hover:bg-rose-600 text-white rounded-md inline-flex items-center gap-1.5" data-testid="delete-event-btn">
                <Trash size={14}/> Eliminar
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1 inline-flex items-center gap-1.5">{Icon && <Icon size={12}/>}{label}</div>
      <div className="font-semibold text-neutral-900">{value}</div>
    </div>
  );
}

function EventWizard({ docs, frequencies, onClose, onSuccess }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0,10));
  const [docIds, setDocIds] = useState([]);
  const [emails, setEmails] = useState("");
  const [selectedFreqs, setSelectedFreqs] = useState([]);
  const [responsible, setResponsible] = useState("");
  const [status, setStatus] = useState("programado");
  const [busy, setBusy] = useState(false);

  const toggleDoc = (id) => setDocIds(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
  const toggleFreq = (name) => setSelectedFreqs(prev => prev.includes(name) ? prev.filter(x=>x!==name) : [...prev, name]);

  const submit = async () => {
    if (!title || !date) { toast.error("Título y fecha son obligatorios"); return; }
    setBusy(true);
    try {
      await api.post("/calendar/events", {
        title, description, event_date: date, responsible, status,
        notify_daily: selectedFreqs.includes("Diaria"),
        notify_weekly: selectedFreqs.includes("Semanal"),
        notify_monthly: selectedFreqs.includes("Mensual"),
        notify_frequencies: selectedFreqs,
        document_ids: docIds,
        notify_emails: emails.split(",").map(e=>e.trim()).filter(Boolean),
      });
      toast.success("Evento creado");
      onSuccess();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="event-wizard-modal">
      <div className="bg-white rounded-md w-full max-w-2xl border border-neutral-200 anim-fade-up max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-5 border-b border-neutral-200">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Asistente</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mt-1">Nuevo evento de calendario</h2>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700"><X size={22}/></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Título</label>
            <input value={title} onChange={(e)=>setTitle(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none" data-testid="event-title-input"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Fecha</label>
              <input type="date" value={date} onChange={(e)=>setDate(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="event-date-input"/>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Estado</label>
              <select value={status} onChange={e=>setStatus(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="event-status-select">
                {STATUS_OPTIONS.map(s=><option key={s.k} value={s.k}>{s.l}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Responsable</label>
              <input value={responsible} onChange={e=>setResponsible(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="event-responsible-input"/>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Correos para notificar</label>
              <input value={emails} onChange={(e)=>setEmails(e.target.value)} placeholder="email1@x.com, email2@x.com" className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="event-emails-input"/>
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Descripción</label>
            <textarea value={description} onChange={(e)=>setDescription(e.target.value)} rows={2} className="w-full px-3 py-2 border border-neutral-200 rounded-md" data-testid="event-description-input"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Frecuencia de notificación (catálogo)</label>
            <div className="flex flex-wrap gap-2">
              {frequencies.length === 0 && <p className="text-xs text-amber-600">Sin frecuencias en catálogo. El Super Admin debe agregarlas en Catálogos → Frecuencias notificación.</p>}
              {frequencies.map(f => (
                <label key={f.id} className={`inline-flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer transition-colors ${selectedFreqs.includes(f.name)?'bg-blue-50 border-blue-300 text-blue-700':'border-neutral-200 hover:bg-neutral-50'}`}>
                  <input type="checkbox" checked={selectedFreqs.includes(f.name)} onChange={()=>toggleFreq(f.name)} data-testid={`freq-${f.name}`}/> {f.name}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Documentos a ligar</label>
            <div className="max-h-48 overflow-y-auto border border-neutral-200 rounded-md p-2 space-y-1">
              {docs.length === 0 && <p className="text-sm text-neutral-400 p-3">No hay documentos disponibles.</p>}
              {docs.map(d => (
                <label key={d.id} className="flex items-center gap-2 p-2 rounded hover:bg-neutral-50 cursor-pointer text-sm">
                  <input type="checkbox" checked={docIds.includes(d.id)} onChange={()=>toggleDoc(d.id)} data-testid={`link-doc-${d.id}`}/>
                  <span className="font-semibold">{d.name}</span>
                  <span className="text-xs text-neutral-500">· {d.period} · {d.authority}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-neutral-200 bg-neutral-50">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 rounded-md hover:bg-white">Cancelar</button>
          <button onClick={submit} disabled={busy} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid="submit-event-btn">
            {busy ? "Creando..." : "Crear evento"}
          </button>
        </div>
      </div>
    </div>
  );
}
